import React, { useContext, useEffect, useState } from "react";
import { useAuthContext } from "../../../contexts/AuthContext";
import { Row } from "react-bootstrap";
import Sidebar from "../../UI/Sidebar";
import { getWebsite } from "../../SF";
import Loader from "../../UI/Loader";
import Filter from "../../UI/Filter";
import { getStorageValue } from "../../Utils";
import "../shared-styles.css";

const isDevelopment = process.env.NODE_ENV === "development";

const Website = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userOptionsOwners, setUserOptionsOwners] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(null);
  const context = useAuthContext();

  const clearLocalStorage = () => {
    try {
      localStorage.clear();
      if (!isDevelopment && window.errorReporting) {
        window.errorReporting.captureEvent({
          message: "localStorage cleared in Website view",
          level: "info",
          extra: { view: "Website" },
        });
      }
    } catch (error) {
      if (!isDevelopment && window.errorReporting) {
        window.errorReporting.captureException(error);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const data = await getWebsite(context);
        const owners = data.owners.map((owner) => ({
          value: owner.id,
          label: owner.name,
        }));

        setUserOptionsOwners(owners);
        setData(data.data);
        setFilteredData(data.data);
      } catch (error) {
        if (!isDevelopment && window.errorReporting) {
          window.errorReporting.captureException(error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Search functionality
  useEffect(() => {
    if (!data) {
      setFilteredData(data);
      return;
    }

    if (!searchTerm.trim()) {
      setFilteredData(data);
      return;
    }

    const filtered = data.filter((account) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        account.name?.toLowerCase().includes(searchLower) ||
        account.websiteAdmin?.toLowerCase().includes(searchLower) ||
        account.stagingSiteStatus?.toLowerCase().includes(searchLower) ||
        account.launchDueDate?.toString().toLowerCase().includes(searchLower) ||
        account.nextTask?.subject?.toLowerCase().includes(searchLower) ||
        account.websiteURL?.toLowerCase().includes(searchLower) ||
        account.stagingSiteURL?.toLowerCase().includes(searchLower) ||
        account.wordpressLoginURL?.toLowerCase().includes(searchLower) ||
        account.wordpressUsername?.toLowerCase().includes(searchLower)
      );
    });

    setFilteredData(filtered);
  }, [data, searchTerm]);

  useEffect(() => {
    try {
      if (getStorageValue("gargle_owner_id"))
        context.OwnerId = getStorageValue("gargle_owner_id");
    } catch (err) {
      context.OwnerId = null;
      console.warn("[localStorage.clear] called in Website/index.js", {
        stack: new Error().stack,
      });
      window.localStorage.clear();
    }
  }, [context]);

  const columns = [
    "Account",
    "Website Admin",
    "Staging Site Status",
    "Launch Due Date",
    "Next Task",
    "Website URL",
    "Staging Site URL",
    "WordPress Login URL",
    "WordPress Username",
    "WordPress Password",
  ];

  if (context.OwnerId)
    context.Owner = userOptionsOwners.find(
      (item) => item.value === context.OwnerId
    );

  if (!context?.ready) return <Loader />;
  if (loading || !data) return <Loader />;

  return (
    <>
      <Sidebar />
      <div className="content">
        <div className="views-container">
          <div className="views-modern-header">
            <div className="views-title-section">
              <div>
                <h1 className="views-page-title">Website</h1>
                <div className="views-subtitle">
                  {!context.OwnerId ||
                  !context.Owner ||
                  context.Owner.value === "null"
                    ? "All Directors"
                    : context.Owner.label}{" "}
                  - {filteredData?.length || 0} accounts
                </div>
              </div>
              <div className="views-filter-section">
                <select
                  className="views-filter-select"
                  value={context.OwnerId || ""}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    context.OwnerId = selectedId || null;
                    window.localStorage.setItem(
                      "gargle_owner_id",
                      selectedId || ""
                    );
                    window.location.reload();
                  }}
                >
                  <option value="">All Directors</option>
                  {userOptionsOwners.map((owner) => (
                    <option key={owner.value} value={owner.value}>
                      {owner.label}
                    </option>
                  ))}
                </select>
                <button
                  className="views-refresh-btn"
                  onClick={() => window.location.reload()}
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          <div className="views-table-container">
            <div className="views-search-container">
              <input
                type="text"
                className="views-search-input"
                placeholder="Search websites, admins, URLs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="views-table-wrapper">
              <table className="views-modern-table wide">
                <thead>
                  <tr>
                    {columns.map((c) => {
                      return <th key={"col-" + c}>{c}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredData?.map((a) => {
                    return (
                      <tr key={"row-" + a.name}>
                        <td>
                          <a
                            className="views-table-link"
                            href={
                              "https://gargleinc.lightning.force.com/" + a.id
                            }
                            target={"_blank"}
                            rel="noreferrer"
                          >
                            {a.name}
                          </a>
                        </td>
                        <td>
                          {a.websiteAdmin && (
                            <>
                              <p>{a.websiteAdmin}</p>
                            </>
                          )}
                        </td>
                        <td>
                          {a.stagingSiteStatus && (
                            <>
                              <p>{a.stagingSiteStatus}</p>
                            </>
                          )}
                        </td>
                        <td>{a.launchDueDate}</td>
                        <td>
                          {a.nextTask && (
                            <>
                              <a
                                className="views-table-link"
                                href={
                                  "https://gargleinc.lightning.force.com/" +
                                  a.nextTask.id
                                }
                                target={"_blank"}
                                rel="noreferrer"
                              >
                                {a.nextTask.subject}
                                <br />
                                {a.nextTask.activityDate}
                              </a>
                            </>
                          )}
                        </td>
                        <td>
                          {a.websiteURL && (
                            <a
                              className="views-table-link"
                              href={a.websiteURL}
                              target={"_blank"}
                              rel="noreferrer"
                            >
                              {a.websiteURL}
                            </a>
                          )}
                        </td>
                        <td>
                          {a.stagingSiteURL && (
                            <a
                              className="views-table-link"
                              href={a.stagingSiteURL}
                              target={"_blank"}
                              rel="noreferrer"
                            >
                              {a.stagingSiteURL}
                            </a>
                          )}
                        </td>
                        <td>
                          {a.wordpressLoginURL && (
                            <a
                              className="views-table-link"
                              href={a.wordpressLoginURL}
                              target={"_blank"}
                              rel="noreferrer"
                            >
                              {a.wordpressLoginURL}
                            </a>
                          )}
                        </td>
                        <td>
                          {a.wordpressUsername && (
                            <>
                              <p>{a.wordpressUsername}</p>
                            </>
                          )}
                        </td>
                        <td>
                          {a.wordpressPassword && (
                            <>
                              <p>{a.wordpressPassword}</p>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Website;
